import { useRef, useState, type DragEvent } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Props = {
  disabled: boolean;
  denied: boolean;
  busy: boolean;
  onFiles: (files: File[]) => void;
};

export default function Dropzone({ disabled, denied, busy, onFiles }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const locked = disabled || busy;

  function takeFiles(files: File[]) {
    if (locked || files.length === 0) return;
    onFiles(files);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    takeFiles(Array.from(event.dataTransfer.files));
  }

  return (
    <div>
      <div
        onDragEnter={(event) => {
          event.preventDefault();
          if (!locked) setDragOver(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!locked) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        aria-disabled={locked}
        className={`flex min-h-56 flex-col items-center justify-center rounded-lg border border-dashed px-6 py-10 text-center ${
          dragOver ? 'border-primary bg-accent' : 'border-border bg-card'
        } ${locked ? 'opacity-60' : ''}`}
      >
        <Upload className="mb-3 h-6 w-6 text-muted-foreground" />
        <p className="text-base font-medium">{busy ? 'Creating batch…' : 'Drop product images here'}</p>
        <p className="mt-2 text-sm text-muted-foreground">or</p>
        <Button
          type="button"
          variant="outline"
          className="mt-2"
          disabled={locked}
          onClick={() => inputRef.current?.click()}
        >
          Choose files
        </Button>
        <p className="mt-4 text-xs text-muted-foreground">
          JPEG · PNG · WebP · GIF · TIFF · AVIF · up to 25 MB each
        </p>
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,image/tiff,image/avif,.jpg,.jpeg,.png,.webp,.gif,.tif,.tiff,.avif"
          disabled={locked}
          onChange={(event) => {
            takeFiles(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />
      </div>
      {denied && (
        <p className="mt-3 text-sm text-muted-foreground">You don’t have permission to add images.</p>
      )}
    </div>
  );
}
